/**
 * cadRads.js — CAD-RADS 2.0: категория, нагрузка бляшками, модификаторы, запись.
 *
 * ЧТО ЭТО. CAD-RADS (Coronary Artery Disease Reporting and Data System) —
 * согласованная система записи результата КТ-коронарографии: одна строка вида
 * `CAD-RADS 3/P2/HRP`, из которой и врач, и кардиолог одинаково понимают степень
 * стеноза, объём бляшек и особенности. Версия 2.0 — консенсус SCCT/ACR/NASCI/AHA
 * 2022 года (RSNA-репринт: pubs.rsna.org/doi/10.1148/ryct.220183).
 *
 * ЧТО ЭТО НЕ. **Здесь нет автоматического поиска стенозов.** Модуль считает
 * категорию из чисел, которые ВВЁЛ ВРАЧ: процент стеноза по сосудам, кальциевый
 * счёт, признаки уязвимой бляшки. Автоматическое измерение стеноза по КТ —
 * отдельная задача (и отдельный класс ответственности), и делать вид, что она
 * решена, нельзя. Кнопка в интерфейсе называется «Калькулятор CAD-RADS» именно
 * поэтому.
 *
 * ГРАНИЦЫ, ВЗЯТЫЕ ИЗ КОНСЕНСУСА ДОСЛОВНО:
 *
 *   0    — 0%, бляшек нет
 *   1    — 1–24%, минимальный
 *   2    — 25–49%, немного (необструктивный)
 *   3    — 50–69%, умеренный
 *   4A   — 70–99% в одном или двух сосудах
 *   4B   — ствол ЛКА ≥50% ИЛИ трёхсосудистое обструктивное (≥70% в каждом)
 *   5    — 100%, полная окклюзия
 *   N    — недиагностическое исследование
 *
 * Нагрузка бляшками (P), по кальциевому счёту Агатстона либо по SIS:
 *   P1: CAC 1–100    или SIS 1–2
 *   P2: CAC 101–300  или SIS 3–4
 *   P3: CAC 301–999  или SIS 5–7
 *   P4: CAC ≥1000    или SIS ≥8
 * При категории 0 нагрузка не присваивается (в консенсусе прямо: «P0 is not
 * required as a classification»).
 *
 * ГДЕ ИСТОЧНИКИ РАСХОДЯТСЯ — сказано вслух, а не спрятано в код:
 *   · Порог P4 по кальцию: в первичном PDF напечатано «>1000», в RadioGraphics
 *     Pictorial Guide — «≥1000». Здесь принято ≥1000 (значение ровно 1000 иначе
 *     не попадает никуда), и это отмечено в оговорках результата.
 *   · Трёхсосудистое для 4B: в тексте «>70%», в таблице «(≥70%)». Принято ≥70%,
 *     потому что 70–99% уже покрыто 4A и разрыва быть не должно.
 *   · Размер «точечного кальцината» (spotty calcification) в первичном
 *     документе числом НЕ задан (во вторичных — то <3 мм, то <4 мм). Поэтому
 *     здесь признак принимается как отмеченный врачом, а число не зашивается.
 *
 * Модификаторы: N (недиагностично), HRP (уязвимая бляшка: нужно ДВА признака из
 * четырёх), I (ишемия по CT-FFR или КТ-перфузии), S (стент), G (шунт),
 * E (неатеросклеротическая причина). Порядок записи из консенсуса:
 * `CAD-RADS [0-5] / P / N / HRP / I / S / G / E`.
 *
 * CT-FFR (пороги из RadioGraphics Pictorial Guide): ≤0,75 — ишемия (I+),
 * >0,80 — норма (I−), 0,76–0,80 — пограничное (I±).
 *
 * И ГЛАВНОЕ, тоже из консенсуса дословно: CAD-RADS не заменяет заключение врача
 * («should not be viewed as a substitute for the impression section») и не
 * заменяет клиническое суждение. Категория 0–5 не ставится при
 * неатеросклеротических причинах — для них модификатор E.
 */

/** Три главных сосуда, по которым считается трёхсосудистое поражение. */
export const ГЛАВНЫЕ_СОСУДЫ = ['ПМЖА', 'ОА', 'ПКА'];

/** Признаки уязвимой бляшки (HRP). Нужно два и более — на одной бляшке. */
export const ПРИЗНАКИ_HRP = {
  'низкая-плотность': 'плотность внутри бляшки < 30 HU',
  'положительное-ремоделирование': 'отношение наружных диаметров > 1,1',
  'точечный-кальцинат': 'точечный кальцинат внутри бляшки',
  'кольцо-салфетки': 'симптом кольца салфетки (napkin-ring)',
};

/** Минимальный диаметр сосуда, к которому применима система (мм). */
export const МИН_ДИАМЕТР_ММ = 1.5;

const ВЕДЕНИЕ = {
  '0': 'Успокоить пациента. Рассмотреть неатеросклеротические причины симптомов.',
  '1': 'Рассмотреть неатеросклеротические причины. При нагрузке P2 и выше — модификация факторов риска и профилактическая лекарственная терапия.',
  '2': 'Модификация факторов риска и профилактическая терапия; при P3–P4 — агрессивная модификация факторов риска.',
  '3': 'Рассмотреть функциональную оценку (CT-FFR, КТ-перфузия или стресс-тест). Агрессивная профилактика, антиангинальная терапия по руководствам.',
  '4A': 'Рассмотреть инвазивную коронарографию или функциональную оценку. Агрессивная терапия; вопрос реваскуляризации по руководствам.',
  '4B': 'Инвазивная коронарография РЕКОМЕНДОВАНА. Агрессивная терапия, вопрос реваскуляризации.',
  '5': 'Рассмотреть инвазивную коронарографию, функциональную оценку и/или оценку жизнеспособности миокарда. Агрессивная терапия.',
  'N': 'Исследование недиагностическое: нужна дополнительная или альтернативная оценка.',
};

const ЧИСЛО = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Категория нагрузки бляшками.
 * @param {{cac?: number|null, sis?: number|null}} вход
 * @returns {{нагрузка: string|null, метод: string|null, оговорки: string[]}}
 */
export function нагрузкаБляшками({ cac = null, sis = null } = {}) {
  const оговорки = [];
  const к = ЧИСЛО(cac);
  const с = ЧИСЛО(sis);

  if (к !== null) {
    if (к < 0) return { нагрузка: null, метод: null, оговорки: ['кальциевый счёт не может быть отрицательным'] };
    if (к === 0) return { нагрузка: null, метод: 'CAC', оговорки: ['кальциевый счёт 0: нагрузка по кальцию не присваивается'] };
    if (к >= 1000) {
      оговорки.push('порог P4 по кальцию в источниках напечатан по-разному (>1000 и ≥1000); здесь ≥1000');
      return { нагрузка: 'P4', метод: 'CAC', оговорки };
    }
    if (к >= 301) return { нагрузка: 'P3', метод: 'CAC', оговорки };
    if (к >= 101) return { нагрузка: 'P2', метод: 'CAC', оговорки };
    return { нагрузка: 'P1', метод: 'CAC', оговорки };
  }

  if (с !== null) {
    if (с < 0 || с > 17) return { нагрузка: null, метод: null, оговорки: ['SIS считается по 16 сегментам (17 при ramus intermedius)'] };
    if (с === 0) return { нагрузка: null, метод: 'SIS', оговорки: ['SIS 0: бляшек не найдено'] };
    if (с >= 8) return { нагрузка: 'P4', метод: 'SIS', оговорки };
    if (с >= 5) return { нагрузка: 'P3', метод: 'SIS', оговорки };
    if (с >= 3) return { нагрузка: 'P2', метод: 'SIS', оговорки };
    return { нагрузка: 'P1', метод: 'SIS', оговорки };
  }

  return { нагрузка: null, метод: null, оговорки: ['нагрузка бляшками не задана: нет ни кальциевого счёта, ни SIS'] };
}

/**
 * Модификатор ишемии по CT-FFR.
 * @returns {'I+'|'I-'|'I±'|null}
 */
export function модификаторИшемии({ ctFFR = null, перфузия = null } = {}) {
  const f = ЧИСЛО(ctFFR);
  if (f !== null) {
    if (f <= 0 || f > 1) return null;                 // недопустимое значение
    if (f <= 0.75) return 'I+';
    if (f >= 0.81) return 'I-';
    return 'I±';                                       // 0,76…0,80
  }
  if (перфузия === 'да') return 'I+';
  if (перфузия === 'нет') return 'I-';
  if (перфузия === 'пограничная') return 'I±';
  return null;
}

/**
 * Основной расчёт.
 *
 * @param {{
 *   сосуды?: Array<{имя: string, стеноз: number, диаметрМм?: number}>,
 *   стволЛКА?: number|null,
 *   недиагностическиеСегменты?: number,
 *   cac?: number|null, sis?: number|null,
 *   признакиHRP?: string[],
 *   ctFFR?: number|null, перфузия?: string|null,
 *   стент?: boolean, шунт?: boolean,
 *   исключение?: string|null,
 * }} вход
 */
export function рассчитать(вход = {}) {
  const оговорки = [];
  const ошибки = [];
  const сосудыВход = Array.isArray(вход.сосуды) ? вход.сосуды : [];

  // Сосуды тоньше 1,5 мм из оценки исключаются — так сказано в консенсусе.
  const сосуды = [];
  for (const с of сосудыВход) {
    const стеноз = ЧИСЛО(с?.стеноз);
    if (стеноз === null || стеноз < 0 || стеноз > 100) {
      ошибки.push(`сосуд «${с?.имя || '?'}»: стеноз должен быть числом 0…100`);
      continue;
    }
    const d = ЧИСЛО(с?.диаметрМм);
    if (d !== null && d < МИН_ДИАМЕТР_ММ) {
      оговорки.push(`«${с.имя}» диаметром ${d} мм исключён: система не применяется к сосудам тоньше ${МИН_ДИАМЕТР_ММ} мм`);
      continue;
    }
    сосуды.push({ имя: String(с.имя || '?'), стеноз, диаметрМм: d });
  }

  const ствол = ЧИСЛО(вход.стволЛКА);
  if (ствол !== null && (ствол < 0 || ствол > 100)) ошибки.push('стеноз ствола ЛКА должен быть числом 0…100');

  const недиаг = Math.max(0, Math.trunc(ЧИСЛО(вход.недиагностическиеСегменты) ?? 0));

  // Исключение (E) — неатеросклеротическая причина. Категория 0–5 в этом случае
  // по консенсусу не ставится.
  const исключение = вход.исключение ? String(вход.исключение) : null;

  const всеСтенозы = [...сосуды.map((с) => с.стеноз), ...(ствол !== null ? [ствол] : [])];
  const максСтеноз = всеСтенозы.length ? Math.max(...всеСтенозы) : (недиаг > 0 ? null : 0);

  // ── Модификаторы ──
  const модификаторы = [];

  const признаки = Array.isArray(вход.признакиHRP) ? вход.признакиHRP.filter((п) => п in ПРИЗНАКИ_HRP) : [];
  const неизвестные = (вход.признакиHRP || []).filter((п) => !(п in ПРИЗНАКИ_HRP));
  if (неизвестные.length) ошибки.push('неизвестные признаки HRP: ' + неизвестные.join(', '));
  const hrp = признаки.length >= 2;
  if (признаки.length === 1) {
    оговорки.push('для HRP нужно два признака и более на одной бляшке; отмечен один — модификатор не ставится');
  }

  const ишемия = модификаторИшемии({ ctFFR: вход.ctFFR, перфузия: вход.перфузия });
  if (вход.ctFFR != null && ишемия === null) ошибки.push('CT-FFR должен быть числом больше 0 и не больше 1');

  // ── Категория ──
  let категория;
  const обструктивные = сосуды.filter((с) => с.стеноз >= 70 && ГЛАВНЫЕ_СОСУДЫ.includes(с.имя)).length;
  const трёхсосудистое = обструктивные >= 3;
  const стволОбструктивный = ствол !== null && ствол >= 50;

  if (ошибки.length && !всеСтенозы.length) {
    категория = null;
  } else if (максСтеноз === null) {
    категория = 'N';                                   // нет ни одного оценённого сосуда, но есть невыводимые
  } else if (максСтеноз >= 100) {
    категория = '5';
    if (стволОбструктивный || трёхсосудистое) {
      оговорки.push('есть и полная окклюзия, и критерии 4B (ствол ЛКА ≥50% или трёхсосудистое): консенсус прямо этот случай не разбирает, здесь взята более тяжёлая категория 5 — 4B указана в пояснении');
    }
  } else if (стволОбструктивный || трёхсосудистое) {
    категория = '4B';
  } else if (максСтеноз >= 70) {
    категория = '4A';
  } else if (максСтеноз >= 50) {
    категория = '3';
  } else if (максСтеноз >= 25) {
    категория = '2';
  } else if (максСтеноз >= 1) {
    категория = '1';
  } else {
    категория = '0';
  }

  // ── Правило N ──
  // Есть невыводимые сегменты: при стенозе ≥50% в выводимых ставится числовая
  // категория и модификатор N, иначе — категория N.
  let модификаторN = false;
  if (недиаг > 0 && категория !== null) {
    if (категория === 'N') {
      // уже N
    } else if (максСтеноз >= 50) {
      модификаторN = true;
    } else {
      оговорки.push(`в выводимых сегментах стеноз ${максСтеноз}% (<50%), но ${недиаг} сегмент(ов) невыводимы — по правилу консенсуса категория N`);
      категория = 'N';
    }
  }

  // ── Нагрузка бляшками ──
  let нагрузка = null, методНагрузки = null;
  if (категория !== '0' && категория !== null) {
    const н = нагрузкаБляшками({ cac: вход.cac, sis: вход.sis });
    нагрузка = н.нагрузка; методНагрузки = н.метод;
    оговорки.push(...н.оговорки);
  } else if (категория === '0') {
    if (ЧИСЛО(вход.cac) > 0 || ЧИСЛО(вход.sis) > 0) {
      оговорки.push('категория 0 означает отсутствие бляшек; заданы кальциевый счёт или SIS больше нуля — проверьте ввод');
    }
  }

  // ── Сборка записи в порядке из консенсуса ──
  if (модификаторN) модификаторы.push('N');
  if (hrp) модификаторы.push('HRP');
  if (ишемия) модификаторы.push(ишемия);
  if (вход.стент) модификаторы.push('S');
  if (вход.шунт) модификаторы.push('G');
  if (исключение) modificatorE();
  function modificatorE() { модификаторы.push('E'); }

  const части = [];
  if (категория !== null) части.push(`CAD-RADS ${категория}`);
  if (нагрузка) части.push(нагрузка);
  части.push(...модификаторы);
  const запись = части.length ? части.join('/') : null;

  if (исключение) {
    оговорки.push(`неатеросклеротическая причина (${исключение}): по консенсусу категория 0–5 в этом случае не применяется, используется модификатор E`);
  }
  if (ишемия && максСтеноз !== null && (максСтеноз < 50 || максСтеноз > 90)) {
    оговорки.push('CT-FFR по консенсусу применим при стенозе 50–90% (прежде всего категории 3 и 4A); при иных стенозах трактовать осторожно');
  }
  оговорки.push('CAD-RADS не заменяет заключение врача и клиническое суждение');
  оговорки.push('расчёт по введённым процентам стеноза: автоматического измерения стеноза здесь нет');

  return {
    категория,
    запись,
    максСтеноз,
    обструктивныхГлавных: обструктивные,
    трёхсосудистое,
    стволОбструктивный,
    нагрузка, методНагрузки,
    модификаторы,
    признакиHRP: признаки,
    ишемия,
    ведение: категория ? ВЕДЕНИЕ[категория] : null,
    оговорки,
    ошибки,
  };
}

export default { рассчитать, нагрузкаБляшками, модификаторИшемии, ГЛАВНЫЕ_СОСУДЫ, ПРИЗНАКИ_HRP, МИН_ДИАМЕТР_ММ };
